// src/store/middleware/persistenceMiddleware.js
// Redux middleware for handling localStorage/sessionStorage persistence

import { authStorage } from "../../utils/authStorage";

export const persistenceMiddleware = store => next => action => {
    const result = next(action);

    // Handle authentication actions
    if (action.type === 'auth/loginSuccess') {
        const { token, user } = action.payload;

        // Store token in sessionStorage (cleared on browser close)
        authStorage.setToken(token);

        // Store user data in localStorage (persists across sessions)
        authStorage.setUser(user);
        authStorage.setLoggedIn(true);

        // Store theme if user has one
        if (user.theme) {
            authStorage.setTheme(user.theme);
        }
    }

    if (action.type === 'auth/logout') {
        // Clear all auth-related storage
        authStorage.clear();
        // Clear cart on logout
        localStorage.removeItem('cart');
    }

    if (action.type === 'auth/setCustomerData') {
        const state = store.getState();
        const customerId = state.auth?.customer_id || authStorage.getCustomerId();
        if (customerId && action.payload) {
            authStorage.setCustomerData(customerId, action.payload);
        }
    }

    // Handle cart actions - save to localStorage
    if (action.type.startsWith('cart/')) {
        const state = store.getState();
        if (state.cart && state.cart.items) {
            localStorage.setItem('cart', JSON.stringify(state.cart.items));
        }
    }

    // Handle theme actions
    if (action.type === 'theme/setTheme') {
        authStorage.setTheme(action.payload);
    }

    return result;
};

// Middleware to restore state from storage on app start
export const rehydrateMiddleware = store => next => action => {
    if (action.type === '@@INIT' || action.type === '@@redux/INIT') {
        // Check if user is still authenticated
        const token = authStorage.getToken();
        const user = authStorage.getUser();
        const isLoggedIn = authStorage.isLoggedIn();

        if (token && user && isLoggedIn) {
            // Restore authentication state
            store.dispatch({
                type: 'auth/loginSuccess',
                payload: { token, user }
            });
            if (user.customer_id) {
                const customerData = authStorage.getCustomerData(user.customer_id);
                if (customerData) {
                    store.dispatch({
                        type: 'auth/setCustomerData',
                        payload: customerData,
                    });
                }
            }
        }
        // Always mark rehydration complete, regardless of login state
        store.dispatch({ type: 'auth/setRehydrated' });

        // Restore theme if available
        const theme = authStorage.getTheme();
        if (theme) {
            store.dispatch({
                type: 'theme/setTheme',
                payload: theme
            });
        }

        // Restore cart from localStorage
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
            try {
                const cartItems = JSON.parse(savedCart);
                store.dispatch({
                    type: 'cart/restoreCart',
                    payload: cartItems,
                });
            } catch (e) {
                console.error('Failed to restore cart from localStorage:', e);
            }
        }
    }

    return next(action);
};
